import React, { useState } from 'react';
import { X, Check, FileText } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const ExportOptionsModal = ({ isOpen, onClose, onExport }) => {
  const { theme } = useTheme();
  const [exportOptions, setExportOptions] = useState({
    format: 'pdf',
    pageSize: 'a4',
    orientation: 'portrait',
    margins: 'normal',
    includeVisualizations: true,
    includeTableOfContents: true,
    optimizeEquations: true,
    addPageBreaks: true,
    contentSections: {
      title: true,
      headings: true,
      body: true,
      equations: true,
      code: true,
    }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setExportOptions(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: checked
          }
        }));
      } else {
        setExportOptions(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setExportOptions(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onExport(exportOptions);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      <div 
        className="relative rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ background: theme.background }}
      >
        <div className="sticky top-0 z-10 px-6 py-4 flex justify-between items-center shadow-sm" 
          style={{ borderBottom: `1px solid ${theme.border}`, background: theme.background }}>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text }}>
            <FileText size={20} />
            Export Options
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-opacity-10 transition-colors"
            style={{ background: `${theme.accent}10` }}
          >
            <X size={20} style={{ color: theme.accent }} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Format Selection */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: theme.text }}>
                Export Format
              </label>
              <select
                name="format"
                value={exportOptions.format}
                onChange={handleChange}
                className="w-full p-2 rounded-lg"
                style={{ 
                  background: theme.secondary,
                  border: `1px solid ${theme.border}`,
                  color: theme.text
                }}
              >
                <option value="pdf">PDF Document</option>
                <option value="html">HTML Document</option>
              </select>
            </div>
            
            {/* Page Settings */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: theme.text }}>
                Page Size
              </label>
              <select
                name="pageSize"
                value={exportOptions.pageSize}
                onChange={handleChange}
                className="w-full p-2 rounded-lg"
                style={{ 
                  background: theme.secondary,
                  border: `1px solid ${theme.border}`,
                  color: theme.text
                }}
              >
                <option value="a4">A4</option>
                <option value="letter">Letter</option>
                <option value="legal">Legal</option>
              </select>
            </div>
            
            {/* Orientation */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: theme.text }}>
                Orientation
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="orientation"
                    value="portrait"
                    checked={exportOptions.orientation === 'portrait'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Portrait</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="orientation"
                    value="landscape"
                    checked={exportOptions.orientation === 'landscape'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Landscape</span>
                </label>
              </div>
            </div>
            
            {/* Margins */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: theme.text }}>
                Page Margins
              </label>
              <select
                name="margins"
                value={exportOptions.margins}
                onChange={handleChange}
                className="w-full p-2 rounded-lg"
                style={{ 
                  background: theme.secondary,
                  border: `1px solid ${theme.border}`,
                  color: theme.text
                }}
              >
                <option value="narrow">Narrow</option>
                <option value="normal">Normal</option>
                <option value="wide">Wide</option>
              </select>
            </div>
            
            {/* Content Options */}
            <div>
              <p className="block mb-2 font-medium" style={{ color: theme.text }}>
                Content Options
              </p>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="includeTableOfContents"
                    checked={exportOptions.includeTableOfContents}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Include table of contents</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="includeVisualizations"
                    checked={exportOptions.includeVisualizations}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Include visualizations</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="optimizeEquations"
                    checked={exportOptions.optimizeEquations}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Optimize equation rendering</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="addPageBreaks"
                    checked={exportOptions.addPageBreaks}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Add smart page breaks at sections</span>
                </label>
              </div>
            </div>
            
            {/* Include/Exclude Content Sections */}
            <div>
              <p className="block mb-2 font-medium" style={{ color: theme.text }}>
                Include Content Sections
              </p>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="contentSections.title"
                    checked={exportOptions.contentSections.title}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Title</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="contentSections.headings"
                    checked={exportOptions.contentSections.headings}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Section Headings</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="contentSections.body"
                    checked={exportOptions.contentSections.body}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Body Text</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="contentSections.equations"
                    checked={exportOptions.contentSections.equations}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Equations</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="contentSections.code"
                    checked={exportOptions.contentSections.code}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span style={{ color: theme.text }}>Code Blocks</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg"
              style={{ 
                background: theme.secondary,
                color: theme.text,
                border: `1px solid ${theme.border}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg flex items-center gap-2"
              style={{ 
                background: theme.primary,
                color: theme.text
              }}
            >
              <Check size={18} />
              Export
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExportOptionsModal; 